/**
 * BPF Engraving Shadow Renderer — Phase 5.1.1 (shadow mode foundation),
 * visual treatment refined Phase 5.2.0, style data normalized out
 * Phase 5.2.2.
 *
 * Development-only. This file is NOT referenced by any <script> tag or
 * asset_url call anywhere in this theme — it never loads for a real
 * customer, on any page, under any condition. It exists to be invoked
 * manually from the browser console while previewing the theme (Shopify
 * Design Mode or a local dev server), by a developer who has pasted or
 * attached this script themselves — and, since Phase 5.2.0, from a real
 * Node canvas implementation too (see the constructor's `createCanvas`
 * option below), so visual-quality verification can produce genuine
 * rendered pixels instead of an approximation.
 *
 * Deliberately a sibling to, not an edit of, assets/bpf-memorial-
 * canvas.js — that file is proven, working, customer-facing code, and
 * this phase's own mission is "zero customer-visible behavior change."
 * Wiring an engraving layer into the real compositor's render() method
 * (a new <img> layer alongside base/figure/capsule, fed by this
 * renderer's output) is deferred to the future phase that actually
 * enables customer-visible engraving — not this one.
 *
 * Rendering technique (two-pass, Canvas 2D only — no SVG, no CSS
 * transforms, no second rendering engine): text is measured and drawn
 * onto a small offscreen "reference" canvas at the profile's own
 * maximumRenderWidth/maximumRenderHeight, then that reference canvas is
 * composited onto the target canvas using ctx.setTransform() with the
 * affine matrix assets/bpf-engraving-geometry.js derives from the
 * profile's geometry — the same technique the geometry module's own
 * header comment documents in full. Unchanged since Phase 5.1.1/5.1.2.
 *
 * Phase 5.2.2 (implements the Phase 5.2.1 normalization review): this
 * file previously hardcoded a per-baseColor ENGRAVING_STYLES table and
 * read fontFamily/fontWeight/letterSpacing/capitalization directly off
 * the (geometry) Engraving Profile. Both were found to be data, not
 * renderer implementation — a new material needing a code deployment
 * here was exactly the anti-pattern this project's own CPD architecture
 * exists to eliminate. render() now takes a resolved Engraving Style
 * Profile (engraving-style-profiles/*.yml, via
 * adapters/builder/resolve-engraving-style-profile.js) as an explicit
 * parameter instead of resolving typography/color internally. The
 * three-layer shadow/fill/highlight draw order, the affine warp, and
 * the clip-path construction are all unchanged — only where their
 * inputs come from changed.
 *
 * Phase 5.2.0 finding worth recording: node-canvas (used for Node-side
 * visual verification) accepts `ctx.letterSpacing` without erroring but
 * does not actually apply it to `measureText()` results, unlike the
 * browser-spec behavior. Rather than depend on inconsistent native
 * behavior across environments, letter-spacing here is applied
 * manually — measured and drawn character-by-character — so the exact
 * same code produces identical results in Node and in a real browser.
 *
 * One narrow, deliberate structural change from Phase 5.1.1: the
 * canvas-creation call is now injected (`options.createCanvas`)
 * instead of hardcoded to `document.createElement`, so this file can
 * run against a real canvas implementation in either environment. This
 * is dependency injection for one environment-specific line, not a
 * change to the resolve -> calculate -> warp pipeline itself — the
 * default browser behavior (and its output) is unchanged.
 */

(function (root, factory) {
  const impl = factory();
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = impl;
  } else {
    root.BpfEngravingShadowRenderer = impl;
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  // Reads one color/material's complete recipe out of a resolved
  // Engraving Style Profile (engraving-style-profiles/*.yml) — no
  // hardcoded values here. A new material is a new key in that YAML's
  // `colors` map, never a change to this file.
  //
  // Phase 5.3.4: each colors[baseColor] entry is now { default,
  // byVaultColor? }, not a bare recipe — readability against a real
  // Vault surface can depend on which surface it's engraved into (a
  // literal color match can be unreadable, e.g. white-on-white), so
  // resolution now takes an optional second axis, vaultColor. This is
  // a structural default/override lookup, not a per-color rule: any
  // colors[x] entry may or may not carry a byVaultColor[y] override,
  // and neither key name is ever compared against a specific color or
  // material name here — a sixth engraving color or a third Vault
  // material needs only new YAML data, never a change to this
  // function. Falls back to `default` whenever no override exists for
  // the given vaultColor, including when vaultColor itself is omitted.
  function resolveColorRecipe(styleProfile, baseColor, vaultColor) {
    const entry = styleProfile.colors && styleProfile.colors[baseColor];
    if (!entry) {
      const available = styleProfile.colors ? Object.keys(styleProfile.colors).join(', ') : '(none)';
      throw new Error(`Engraving style profile "${styleProfile.id}" has no recipe for baseColor "${baseColor}" — expected one of ${available}`);
    }
    const override = vaultColor && entry.byVaultColor ? entry.byVaultColor[vaultColor] : null;
    const recipe = override || entry.default;
    if (!recipe) {
      throw new Error(`Engraving style profile "${styleProfile.id}" has no default recipe for baseColor "${baseColor}"`);
    }
    return recipe;
  }

  function defaultCreateCanvas(width, height) {
    const c = document.createElement('canvas');
    c.width = width;
    c.height = height;
    return c;
  }

  function applyCapitalization(text, capitalization) {
    if (capitalization === 'uppercase') return text.toUpperCase();
    return text;
  }

  // Manual letter-spacing: measured and drawn character-by-character,
  // rather than relying on ctx.letterSpacing (see module header for why).
  function measureWithSpacing(ctx, text, letterSpacing) {
    let width = 0;
    for (const ch of text) width += ctx.measureText(ch).width;
    width += (letterSpacing || 0) * Math.max(text.length - 1, 0);
    return width;
  }

  function fillTextWithSpacing(ctx, text, x, y, letterSpacing, align) {
    const totalWidth = measureWithSpacing(ctx, text, letterSpacing);
    let cursorX;
    if (align === 'left') cursorX = x;
    else if (align === 'right') cursorX = x - totalWidth;
    else cursorX = x - totalWidth / 2; // center

    const savedAlign = ctx.textAlign;
    ctx.textAlign = 'left';
    for (const ch of text) {
      ctx.fillText(ch, cursorX, y);
      cursorX += ctx.measureText(ch).width + (letterSpacing || 0);
    }
    ctx.textAlign = savedAlign;
  }

  class BpfEngravingShadowRenderer {
    constructor(options) {
      const opts = options || {};
      this._geometry = opts.geometry || (typeof window !== 'undefined' ? window.BpfEngravingGeometry : undefined);
      if (!this._geometry) {
        throw new Error(
          'BpfEngravingShadowRenderer requires assets/bpf-engraving-geometry.js to be loaded first (pass { geometry } explicitly, or load it onto window before constructing).',
        );
      }
      this._createCanvas = opts.createCanvas || defaultCreateCanvas;
    }

    _fontString(fontWeight, fontSizePx, fontFamily) {
      const weight = fontWeight && fontWeight !== 'normal' ? `${fontWeight} ` : '';
      return `${weight}${fontSizePx}px ${fontFamily || 'sans-serif'}`;
    }

    // Real text measurement — the counterpart to the Node verification
    // harness's deliberately-labeled stub measurer
    // (adapters/builder/shadow-mode-engraving.js, geometry-only checks).
    _measureTextWidth(text, fontSizePx, fontFamily, fontWeight, letterSpacing) {
      const measureCanvas = this._createCanvas(1, 1);
      const ctx = measureCanvas.getContext('2d');
      ctx.font = this._fontString(fontWeight, fontSizePx, fontFamily);
      return measureWithSpacing(ctx, text, letterSpacing);
    }

    // Draws one text layer (shadow, fill, or highlight) onto its own
    // fresh reference canvas, then composites it onto the target via
    // the same affine transform for every layer — so all three stay
    // pixel-perfectly aligned with each other and with the geometry.
    _compositeLayer(targetCtx, layout, geometryProfile, styleProfile, transform, offsetX, offsetY, color, opacity, blend) {
      const referenceCanvas = this._createCanvas(geometryProfile.maximumRenderWidth, geometryProfile.maximumRenderHeight);
      const refCtx = referenceCanvas.getContext('2d');
      refCtx.textBaseline = layout.textBaseline || 'middle';
      refCtx.font = this._fontString(styleProfile.fontWeight, layout.fontSizePx, styleProfile.fontFamily);
      refCtx.fillStyle = color;
      layout.lines.forEach((line) => {
        fillTextWithSpacing(refCtx, line.text, line.x + offsetX, line.y + offsetY, styleProfile.letterSpacing, layout.alignment);
      });

      targetCtx.save();
      targetCtx.globalAlpha = opacity;
      targetCtx.globalCompositeOperation = blend;
      targetCtx.setTransform(transform.a, transform.b, transform.c, transform.d, transform.e, transform.f);
      targetCtx.drawImage(referenceCanvas, 0, 0);
      targetCtx.restore();
    }

    // Renders geometryProfile (an Engraving Profile — geometry only) +
    // styleProfile (an Engraving Style Profile — typography and visual
    // appearance) + text onto targetWidth x targetHeight, using a
    // three-layer shadow/fill/highlight composite to read as engraved
    // rather than printed. `options.baseColor` selects which color/
    // material recipe within styleProfile.colors to use; `options.
    // vaultColor` (Phase 5.3.4, optional) additionally selects a
    // contrast-adjusted override for that color when one exists for
    // the surface it's being engraved onto — see resolveColorRecipe.
    // `options.targetCanvas`, if provided, is drawn onto directly
    // instead of a fresh blank canvas — required for the multiply/
    // screen blend layers to interact with real plaque pixels rather
    // than transparency; the default (no option passed) is a fresh
    // detached canvas, unchanged from Phase 5.1.1/5.1.2. Returns the
    // canvas, a data URL for quick inspection, and the underlying
    // layout for diagnostics.
    render(geometryProfile, styleProfile, text, targetWidth, targetHeight, options) {
      const opts = options || {};
      const baseColor = opts.baseColor || 'white';
      const recipe = resolveColorRecipe(styleProfile, baseColor, opts.vaultColor);

      const displayText = applyCapitalization(text, styleProfile.capitalization);
      // Deliberately ignores the third (fontFamily) argument the
      // geometry module passes here — it reads that from
      // geometryProfile.fontFamily internally (assets/bpf-engraving-
      // geometry.js, unchanged), which is correctly undefined now that
      // fontFamily lives only on the Engraving Style Profile. Using
      // styleProfile.fontFamily via closure instead is what makes
      // measurement and drawing agree — see the Phase 5.2.2 report for
      // the mismatch this caught during verification.
      const layout = this._geometry.calculateEngravingLayout(
        geometryProfile,
        displayText,
        targetWidth,
        targetHeight,
        (t, size) => this._measureTextWidth(t, size, styleProfile.fontFamily, styleProfile.fontWeight, styleProfile.letterSpacing),
      );

      const targetCanvas = opts.targetCanvas || this._createCanvas(targetWidth, targetHeight);
      const targetCtx = targetCanvas.getContext('2d');

      targetCtx.save();
      if (layout.clipPath) {
        const keys = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
        targetCtx.beginPath();
        keys.forEach((k, i) => {
          const p = layout.clipPath[k];
          if (i === 0) targetCtx.moveTo(p.x, p.y);
          else targetCtx.lineTo(p.x, p.y);
        });
        targetCtx.closePath();
        targetCtx.clip();
      }

      const t = layout.transform;
      // Layer order: shadow first (establishes the "into the material"
      // depth on the lower-right edge), fill second (the visible
      // recessed letterform), highlight last (the lit rim catch-light
      // on the upper-left edge, on top so it reads crisply). Unchanged
      // since Phase 5.2.0 — only the source of each value changed.
      this._compositeLayer(targetCtx, layout, geometryProfile, styleProfile, t, recipe.shadow.offsetPx, recipe.shadow.offsetPx, recipe.shadow.color, recipe.shadow.opacity, recipe.shadow.blend);
      this._compositeLayer(targetCtx, layout, geometryProfile, styleProfile, t, 0, 0, recipe.fill.color, recipe.fill.opacity, 'source-over');
      this._compositeLayer(targetCtx, layout, geometryProfile, styleProfile, t, -recipe.highlight.offsetPx, -recipe.highlight.offsetPx, recipe.highlight.color, recipe.highlight.opacity, recipe.highlight.blend);

      targetCtx.restore();

      return {
        canvas: targetCanvas,
        dataUrl: typeof targetCanvas.toDataURL === 'function' ? targetCanvas.toDataURL('image/png') : undefined,
        layout,
        recipe,
        baseColor,
        displayText,
      };
    }

    // Convenience: renders one named product's entry from an already-
    // fetched copy of the dev-only artifact (assets/bpf-engraving-
    // profile.dev.json), if both its geometry and style are ready.
    // Deliberately does not fetch the artifact itself — this file has
    // no guaranteed, verified CDN URL to fetch from, since it is
    // intentionally referenced by no Liquid file (see assets/bpf-
    // engraving-profile.dev.json's own generator comment). In Shopify
    // Design Mode, a developer gets the artifact's contents by
    // temporarily adding
    // {{ 'bpf-engraving-profile.dev.json' | asset_url }} to any
    // Design-Mode-only debug snippet and fetching that URL, or simply
    // by opening the file's raw contents directly. Throws a clear,
    // specific message (not a generic failure) if either isn't ready —
    // expected for the real Pitbull Vault entry today (geometry is
    // calibrated; style is draft, neither is "active"/"approved" yet).
    renderFromArtifact(artifact, productKey, text, targetWidth, targetHeight, options) {
      const entry = artifact.profiles[productKey];
      if (!entry) {
        throw new Error(`No engraving profile in the dev artifact for "${productKey}".`);
      }
      if (!entry.calibrated) {
        throw new Error(
          `Engraving profile "${entry.geometryRef}" is not calibrated yet (status: ${entry.geometryProfile.status}). ` +
            'See docs/engraving-profiles-README.md for the calibration workflow — this is expected until a reference overlay image is supplied.',
        );
      }
      if (!entry.styleProfile) {
        throw new Error(`No engraving style profile resolved for "${productKey}" — see docs/engraving-style-profiles-README.md.`);
      }
      return this.render(entry.geometryProfile, entry.styleProfile, text, targetWidth, targetHeight, options);
    }
  }

  return BpfEngravingShadowRenderer;
});
